import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { Route, Routes, Navigate } from "react-router-dom";
import { connect } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Import Routes all
import {
  customerRoutes,
  restaurantRoutes,
  adminRoutes,
  authRoutes,
} from "./routes/allRoutes";

// Import all middleware
import Authmiddleware from "./routes/middleware/Authmiddleware";
import AuthRoute from "./routes/middleware/AuthRoute";
import EnvironmentRouteGuard from "./routes/middleware/EnvironmentRouteGuard";

// layouts Format
import VerticalLayout from "./components/VerticalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";
import RestaurantLayout from "./components/RestaurantAdminLayout";
import AdminLayout from "./components/RestaurantAdminLayout/AdminLayout";

// Pages
import Pages404 from "./pages/Utility/pages-404";

// Components
import NotificationPermissionModal from "./components/Common/NotificationPermissionBanner";
import NetworkErrorModal from "./components/Common/NetworkErrorModal";
import LegalBusinessNameModal from "./components/Common/LegalBusinessNameModal";
import {
  NetworkErrorProvider,
  useNetworkError,
} from "./contexts/NetworkErrorContext";
import { get, setNetworkErrorCallback, post } from "./api/axios";
import endpoints from "./api/endpoints";

// Import scss
import "./assets/scss/theme.scss";
import "./assets/scss/custom/components/_notification-banner.scss";

// Import toastr interceptor to suppress network error toasts
import "./utils/toastrInterceptor";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// Import Firebase Configuration file
// import { initFirebaseBackend } from "./helpers/firebase_helper"

// Import Fack Backend
import fakeBackend from "./helpers/AuthType/fakeBackend";

// Config
import config from "./config";

// Firebase Messaging
import {
  initializeFirebaseMessaging,
  deleteFCMToken,
} from "./helpers/firebaseMessaging";

// Activating fake backend
fakeBackend();

// Setup React Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      cacheTime: 10 * 60 * 1000, // 10 minutes - data kept in cache
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch on component mount if data is fresh
      retry: 1, // Retry failed requests once
    },
  },
});

// const firebaseConfig = {
//   apiKey: process.env.REACT_APP_APIKEY,
//   authDomain: process.env.REACT_APP_AUTHDOMAIN,
//   databaseURL: process.env.REACT_APP_DATABASEURL,
//   projectId: process.env.REACT_APP_PROJECTID,
//   storageBucket: process.env.REACT_APP_STORAGEBUCKET,
//   messagingSenderId: process.env.REACT_APP_MESSAGINGSENDERID,
//   appId: process.env.REACT_APP_APPID,
//   measurementId: process.env.REACT_APP_MEASUREMENTID,
// }

// init firebase backend
// initFirebaseBackend(firebaseConfig)

const AppContent = (props) => {
  const { showNetworkError, hideNetworkError, isOffline } = useNetworkError();
  const [hasAutoRetried, setHasAutoRetried] = useState(false);

  // Set up network error callback for axios interceptor
  useEffect(() => {
    setNetworkErrorCallback(showNetworkError);
  }, [showNetworkError]);

  // Reset auto-retry flag when modal opens
  useEffect(() => {
    if (isOffline) {
      setHasAutoRetried(false);
    }
  }, [isOffline]);

  // Handle retry for NetworkErrorModal
  const handleNetworkRetry = async (isAuto = false, isOnlineEvent = false) => {
    // Reload when internet comes back automatically
    if (isOnlineEvent) {
      window.location.reload();
      return;
    }

    if (isAuto) {
      // Only auto-reload once (from 60-second timer)
      if (!hasAutoRetried) {
        setHasAutoRetried(true);
        window.location.reload();
      }
    } else {
      // Manual retry always reloads
      window.location.reload();
    }
  };

  // Check if user is logged in
  const token = localStorage.getItem("accessToken");

  // Get user role from Redux to determine which routes to render
  const { role } = props;

  // Fallback to localStorage if Redux role is not available
  let userRole = role;
  if (!role) {
    const userStr = localStorage.getItem("user");
    let user = null;
    try {
      user = userStr ? JSON.parse(userStr) : null;
      if (user?.is_admin === "1" || user?.is_admin === true) {
        userRole = "admin";
      } else if (
        user?.is_resturant === true ||
        user?.is_resturant === "1" ||
        user?.is_restaurant === true ||
        user?.is_restaurant === "1"
      ) {
        userRole = "restaurant";
      } else {
        userRole = "customer";
      }
    } catch (e) {
      console.error("Failed to parse user data:", e);
      userRole = "customer";
    }
  }

  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [checkingNotificationSettings, setCheckingNotificationSettings] =
    useState(true);
  const [showLegalBusinessNameModal, setShowLegalBusinessNameModal] =
    useState(false);
  const [checkingLegalBusinessName, setCheckingLegalBusinessName] =
    useState(false);

  // Initialize Firebase messaging on mount
  useEffect(() => {
    const initMessaging = async () => {
      if (token && (userRole === "customer" || userRole === "restaurant")) {
        try {
          await initializeFirebaseMessaging((payload) => {
            console.log("Foreground notification received:", payload);
            // Handle foreground notifications here
            // You can dispatch an action to update notification count, etc.
          }, userRole);
        } catch (error) {
          console.error("Failed to initialize Firebase messaging:", error);
        }
      }
    };

    initMessaging();

    // Cleanup on unmount
    return () => {
      // Unsubscribe from messaging if needed
    };
  }, [token, userRole]);

  // Check notification settings before showing permission modal
  useEffect(() => {
    const checkNotificationSettings = async () => {
      if (token && (userRole === "customer" || userRole === "restaurant")) {
        try {
          const endpoint =
            userRole === "restaurant"
              ? endpoints.restaurantNotificationSettings
              : endpoints.notificationSettings;
          const response = await get(endpoint);
          if (response.data.success) {
            const settings = response.data.data;
            // Only show modal if push notifications are enabled AND permission is default
            if (
              settings.push_notifications_enabled &&
              Notification.permission === "default"
            ) {
              setTimeout(() => {
                setShowNotificationModal(true);
              }, 3000);
            }
          }
        } catch (error) {
          console.error("Failed to check notification settings:", error);
        }
      }
      setCheckingNotificationSettings(false);
    };

    checkNotificationSettings();
  }, [token, userRole]);

  // Delete FCM token on logout
  useEffect(() => {
    if (!token) {
      deleteFCMToken();
    }
  }, [token]);

  // Check legal business name for restaurant users
  useEffect(() => {
    const checkLegalBusinessName = async () => {
      if (token && userRole === "restaurant") {
        setCheckingLegalBusinessName(true);
        try {
          const response = await get(endpoints.restaurantMe);
          if (response.data.success) {
            const currentRestaurant = response.data.data.current_restaurant;
            const legalBusinessName = currentRestaurant?.legal_business_name;

            // Show modal if legal business name is missing or empty
            if (!legalBusinessName || legalBusinessName.trim() === "") {
              setShowLegalBusinessNameModal(true);
            }
          }
        } catch (error) {
          console.error("Failed to check legal business name:", error);
        } finally {
          setCheckingLegalBusinessName(false);
        }
      }
    };

    checkLegalBusinessName();
  }, [token, userRole]);

  // Handle legal business name submission
  const handleLegalBusinessNameSubmit = async (legalBusinessName) => {
    try {
      const response = await post(endpoints.restaurantDashboardProfile, {
        legal_business_name: legalBusinessName,
      });

      if (response.data.success) {
        toastr.success("Legal business name updated successfully!");
        setShowLegalBusinessNameModal(false);
      } else {
        throw new Error(
          response.data.message || "Failed to update legal business name",
        );
      }
    } catch (error) {
      console.error("Failed to update legal business name:", error);
      throw error;
    }
  };

  return (
    <GoogleOAuthProvider clientId={config.google.CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <React.Fragment>
          {/* Network Error Modal - global modal for network errors */}
          <NetworkErrorModal
            open={isOffline}
            onRetry={handleNetworkRetry}
            onDismiss={hideNetworkError}
            dismissible={true}
            autoRetrySeconds={60}
          />

          {/* Legal Business Name Modal - mandatory for restaurant users */}
          {!checkingLegalBusinessName &&
            showLegalBusinessNameModal &&
            token &&
            userRole === "restaurant" && (
              <LegalBusinessNameModal
                isOpen={showLegalBusinessNameModal}
                onSubmit={handleLegalBusinessNameSubmit}
              />
            )}

          {/* Notification Permission Modal - only show if settings allow and permission is default */}
          {!checkingNotificationSettings &&
            showNotificationModal &&
            token &&
            userRole === "customer" && (
              <NotificationPermissionModal
                onPermissionGranted={() => {
                  console.log("Notification permission granted");
                  setShowNotificationModal(false);
                }}
                onPermissionDenied={() => {
                  console.log("Notification permission denied");
                  setShowNotificationModal(false);
                }}
              />
            )}

          <Routes>
            {/* Root route - redirect to appropriate dashboard based on role */}
            <Route
              path="/"
              element={
                token ? (
                  userRole === "admin" ? (
                    <Navigate to="/admin/dashboard" replace />
                  ) : userRole === "restaurant" ? (
                    <Navigate to="/restaurant/dashboard" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Auth routes - redirect logged-in users to their dashboard */}
            {authRoutes.map((route, idx) => (
              <Route
                key={idx}
                path={route.path}
                element={
                  <EnvironmentRouteGuard>
                    <AuthRoute>
                      <NonAuthLayout>{route.component}</NonAuthLayout>
                    </AuthRoute>
                  </EnvironmentRouteGuard>
                }
                isAuthProtected={false}
              />
            ))}

            {/* Customer Routes */}
            {customerRoutes.map((route, idx) => (
              <Route
                key={idx}
                path={route.path}
                element={
                  <EnvironmentRouteGuard>
                    <Authmiddleware>
                      <VerticalLayout>{route.component}</VerticalLayout>
                    </Authmiddleware>
                  </EnvironmentRouteGuard>
                }
                isAuthProtected={true}
                exact
              />
            ))}

            {/* Restaurant Routes */}
            {restaurantRoutes.map((route, idx) => (
              <Route
                key={idx}
                path={route.path}
                element={
                  <EnvironmentRouteGuard>
                    <Authmiddleware>
                      <RestaurantLayout>{route.component}</RestaurantLayout>
                    </Authmiddleware>
                  </EnvironmentRouteGuard>
                }
                isAuthProtected={true}
                exact
              />
            ))}

            {/* Admin Routes */}
            {adminRoutes.map((route, idx) => (
              <Route
                key={idx}
                path={route.path}
                element={
                  <EnvironmentRouteGuard>
                    <Authmiddleware>
                      <AdminLayout>{route.component}</AdminLayout>
                    </Authmiddleware>
                  </EnvironmentRouteGuard>
                }
                isAuthProtected={true}
                exact
              />
            ))}

            {/* Catch-all 404 route */}
            <Route path="*" element={<Pages404 />} />
          </Routes>
        </React.Fragment>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
};

const App = (props) => {
  return (
    <NetworkErrorProvider>
      <AppContent {...props} />
    </NetworkErrorProvider>
  );
};

App.propTypes = {
  layout: PropTypes.any,
};

const mapStateToProps = (state) => {
  return {
    layout: state.Layout,
    role: state.Login.role,
  };
};

export default connect(mapStateToProps, null)(App);
